/* eslint-disable max-len */
import { PROJECTS, PRODUCTS, PROFESSIONALS, IMAGES, ARTICLE_ARCHIVE } from '../../utils/constants'
/* This file has 3 main sections:
  1) Common filters related constants.
  2) Set of arrays that modify the UI and functionalities of filters, dropdown, lists, etc.
  3) Set of arrays to display the filters in the correct order.
*/

// Section 1. Common constants

/* Filters type */
export const AREA = 'area'
export const MAX_AREA = 'max_area'
export const MIN_AREA = 'min_area'
export const BIM = 'bim'
export const CATEGORIES = 'categories'
export const CATEGORIES_TREE = 'categories_tree'
export const CAT_1 = `${CATEGORIES}_1`
export const CAT_2 = `${CATEGORIES}_2`
export const CAT_3 = `${CATEGORIES}_3`
export const CAT_4 = `${CATEGORIES}_4`
export const COLOR = 'color'
export const CONSTRUCTION = 'construction'
export const COMPANIES = 'companies'
export const COUNTRY = 'country'
export const COUNTRIES = 'countries'
export const STATES = 'states'
export const ENVIRONMENT = 'environment'
export const INTERIOR = 'interior'
export const MATERIALS = 'materials'
export const OFFICES = 'offices'
export const PROPERTIES = 'properties'
export const PRODUCT_TYPE = 'product_type'
export const SPACE = 'space'
export const TYPE = 'image_type'
export const USE = 'use'
export const YEAR = 'year'
export const BUILDING_ELEMENT = 'building_element'
export const FIXTURES_AND_FURNITURE = 'fixtures_and_furniture'
export const EXPERTISE = 'expertise'
export const EXPERTISE_TREE = `${EXPERTISE}_tree`
export const EXP_1 = `${EXPERTISE}_1`
export const EXP_2 = `${EXPERTISE}_2`
export const AUTHOR = 'author'
export const TAG = 'tag'
export const TAG_TREE = 'tag_tree'
export const TOPIC = 'topic' // Topic is a collection of tags
export const SORT = 'sort'

/* Custom keys to use already declared filters but with a different translation */
export const PROJECT_CATEGORY = 'projectCategory'
export const PROJECTS_CATEGORY = 'projectsCategory'
export const ACTIVITY_COUNTRY = 'activityCountry'
export const PROJECTS_AREA = 'projectsArea'
/* Area filter related (units, identifiers, jsx) */
export const FEET = 'FEET'
export const METER = 'METER'
export const MIN = 'min'
export const MAX = 'max'
export const UNIT_LABELS = {
  [FEET]: 'ft<sup>2</sup>',
  [METER]: 'm<sup>2</sup>'
}
export const AREA_FILTERS = [MIN_AREA, MAX_AREA]

/* Regex to accept only numbers in the Area Filter input */
export const INPUT_AREA_REGEX = /^[0-9]+$/

// Section 2. Sets of arrays to add/remove functionalities

/* Set of available filters used by default in "urlPathToObject" helper function */
// NOTE: If you are creating a new Filter you must add it here
export const FILTERS_KEYS_ARR = [
  YEAR,
  OFFICES,
  CATEGORIES,
  MATERIALS,
  COUNTRY,
  COUNTRIES,
  COLOR,
  MAX_AREA,
  MIN_AREA,
  PROPERTIES,
  USE,
  COMPANIES,
  BIM,
  PRODUCT_TYPE,
  FIXTURES_AND_FURNITURE,
  TYPE,
  BUILDING_ELEMENT,
  SPACE,
  ENVIRONMENT,
  EXPERTISE,
  STATES,
  AUTHOR,
  TAG,
  TOPIC
]

/* Array with available filter keys of the project landing to use in the request of the Professionals widget(In the projects landing) */
export const PROFESSIONALS_FILTERS_WIDGET = [CATEGORIES, COUNTRY, MAX_AREA, MIN_AREA]

/* Dropdown Filter with a full width UI */
export const BIG_FILTERS = [CATEGORIES, COUNTRY, OFFICES, COMPANIES, CAT_1, EXP_1, TAG, AUTHOR]

/*  Dropdown Filter with a small fixed width UI */
export const SMALL_FILTERS = [YEAR, MATERIALS, AREA, COLOR, CAT_2]

/*  A big filter with only one list inside the dropdown, instead of two */
export const ONE_BIG_COLUMN_FILTERS = [COMPANIES, OFFICES, AUTHOR]

/*  Filters that have a different functionality */
// E.g: BIM filter doesn't open a dropdown, instead toggles a state and shows only products with BIM files
export const STANDALONE_FILTERS = [BIM]

/*  Dropdown Filter with a small fixed width UI and are sorted ascendingly */
export const SMALL_ASCENDING_FILTERS = [MATERIALS, USE, PRODUCT_TYPE, PROPERTIES, CAT_2, CAT_3, CAT_4, EXP_2, SPACE, CONSTRUCTION, INTERIOR, ENVIRONMENT, BUILDING_ELEMENT, FIXTURES_AND_FURNITURE, TYPE, STATES, AUTHOR]

/*  Filter items without the searchbar in the dropdown */
export const ITEMS_WITHOUT_SEARCHBAR = [AREA, COLOR, TYPE, SORT]

/*  Group of categories filters */
export const CATEGORIES_TYPES = [CAT_1, CAT_2, CAT_3, CAT_4]

/*  Group of expertise filters */
export const EXPERTISE_TYPES = [EXP_1, EXP_2]

/*  Group of filters that have category levels */
export const LEVEL_CATEGORIES_TYPES = [...CATEGORIES_TYPES, ...EXPERTISE_TYPES]

/*  Group of filters that are category level 1 */
export const KEYS_CAT_LEVEL_ONE = [CAT_1, EXP_1]

/*  Group of filters that are category level 2 */
export const KEYS_CAT_LEVEL_TWO = [CAT_2, EXP_2]

/*  Main filters that have a category levels */
export const CATEGORIES_LEVEL_FILTERS = [CATEGORIES, EXPERTISE]

/* Type of landings that have a category levels */
export const CATEGORIES_LEVEL_TYPES = [PRODUCTS, PROFESSIONALS]

/*  Filters that have two category levels */
export const CATEGORIES_WITH_TWO_LEVELS = [EXP_1]

/*  Filters per landing that are treated as level categories */
export const FILTERS_CATEGORIES_KEYS = {
  [PRODUCTS]: CATEGORIES,
  [PROFESSIONALS]: EXPERTISE
}

/*  Set of filters per landing that are handled as level categories */
export const FILTERS_CATEGORIES_ARRAY = {
  [PRODUCTS]: CATEGORIES_TYPES,
  [PROFESSIONALS]: EXPERTISE_TYPES
}

/*  Group of filters that have a category tree */
export const CATEGORIES_TREE_FILTERS = [CATEGORIES, EXP_1, TAG]

/* Type of landings that have a category tree */
export const CATEGORIES_TREE_TYPES = [PROJECTS, PROFESSIONALS, ARTICLE_ARCHIVE]

/*  Set of filters per landing that have a category tree */
export const FILTERS_TREE_KEYS = {
  [PROJECTS]: {
    [CATEGORIES]: CATEGORIES_TREE
  },
  [PROFESSIONALS]: {
    [EXP_1]: EXPERTISE_TREE,
    [CATEGORIES]: CATEGORIES_TREE
  },
  [ARTICLE_ARCHIVE]: {
    [TAG]: TAG_TREE
  }
}

// Section 3. Sets of arrays to define the filters UI order

/* Arrays with the filters types in the correct order to display in the UI */
export const PROJECT_BIG_FILTERS = [CATEGORIES, COUNTRY, OFFICES, COMPANIES]
export const PROJECT_DESKTOP_FILTERS_ORDER = [...PROJECT_BIG_FILTERS, ...SMALL_FILTERS]
export const PROJECT_MOBILE_FILTERS_ORDER = [...PROJECT_BIG_FILTERS, SMALL_FILTERS]
export const PRODUCTS_FILTERS_ORDER = [...CATEGORIES_TYPES, COUNTRY, BIM, COMPANIES, PROPERTIES, PRODUCT_TYPE, USE, MATERIALS]
export const IMAGES_FILTERS_ORDER = [TYPE, CATEGORIES, SPACE, BUILDING_ELEMENT, FIXTURES_AND_FURNITURE, MATERIALS, ENVIRONMENT, COUNTRY, AREA, OFFICES]
export const PROFESSIONALS_FILTERS_ORDER = [...EXPERTISE_TYPES, COUNTRY, STATES, CATEGORIES, AREA]
export const ARTICLE_ARCHIVE_FILTERS_ORDER = [TAG, YEAR, AUTHOR]
export const ASYNC_FILTERS = [OFFICES]

/* Object with key(type of landing), and an array with strings in the right order to be displayed in Desktop version */
export const FILTERS_DESKTOP_ORDER = {
  [PROJECTS]: PROJECT_DESKTOP_FILTERS_ORDER,
  [PRODUCTS]: PRODUCTS_FILTERS_ORDER,
  [PROFESSIONALS]: PROFESSIONALS_FILTERS_ORDER,
  [IMAGES]: IMAGES_FILTERS_ORDER,
  [ARTICLE_ARCHIVE]: ARTICLE_ARCHIVE_FILTERS_ORDER
}
/* Object with key(type of landing), and an array with strings in the right order to be displayed in Mobile version */
export const FILTERS_MOBILE_ORDER = {
  [PROJECTS]: PROJECT_MOBILE_FILTERS_ORDER,
  [PRODUCTS]: PRODUCTS_FILTERS_ORDER,
  [PROFESSIONALS]: PROFESSIONALS_FILTERS_ORDER,
  [IMAGES]: IMAGES_FILTERS_ORDER,
  [ARTICLE_ARCHIVE]: ARTICLE_ARCHIVE_FILTERS_ORDER
}

/* Array with the order of the colors to display in the Color filter */
// NOTE: These strings colors are mapped to a hexadecimal color in the color-filters.scss file
export const FIXED_COLORS = ['blue', 'green', 'red', 'yellow', 'pink', 'orange', 'brown', 'purple', 'teal', 'gray', 'white', 'black']